import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Pencil, RotateCcw, Send, Undo2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AdminPage, DESTRUCTIVE_OUTLINE, EmptyState, RelativeTime, SectionCard, formatDateTime } from '../../../components/design-system/admin';
import { ColourSwatch } from '../../../components/design-system/common/ColourSwatch';
import { ProductImageTile } from '../../../components/design-system/common/ProductImageTile';
import { SizeLabel } from '../../../components/design-system/common/SizeLabel';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import type { ProductDetail, ProductRetailerLink } from '../../../common/hooks/network-admin.types';
import type { NetworkCollection, NetworkLinkStatus } from '../../../common/models/network.model';
import { ProductEditDialog } from '../../../modules/superadmin/network/ProductEditDialog';
import { ErrorNote, FieldRow, LinkStatusBadge, OverallScoreChip, ProductStatusBadge, methodLabel, vocabLabel } from '../../../modules/superadmin/network/network-parts';

/** The pictures a network product has, each with what it is. */
function mediaOf(d: ProductDetail, t: TFunction): { url: string; label: string }[] {
    const m = d.product.media || ({} as ProductDetail['product']['media']);
    const out: { url: string; label: string }[] = [];
    const push = (url: string | undefined | null, label: string) => {
        if (url && !out.some((x) => x.url === url)) out.push({ url, label });
    };
    push(m.textureUrl, t('ADMIN_NETWORK.MEDIA.TEXTURE'));
    push(m.thumbnailUrl, t('ADMIN_NETWORK.MEDIA.THUMBNAIL'));
    (m.plankImageUrls || []).forEach((u, i) => push(u, t('ADMIN_NETWORK.MEDIA.PLANK', { index: i + 1 })));
    (m.galleryUrls || []).forEach((u, i) => push(u, t('ADMIN_NETWORK.MEDIA.GALLERY', { index: i + 1 })));
    push(d.imageUrl, t('ADMIN_NETWORK.MEDIA.IMAGE'));
    return out;
}

/**
 * One network product: its pictures, its attributes, and every shop that
 * sells it — with the matcher's suggestions to confirm or reject right here.
 */
export function NetworkProductPage() {
    const { t } = useTranslation();
    const { id = '' } = useParams<{ id: string }>();
    const api = useNetworkAdminApi();
    const [detail, setDetail] = useState<ProductDetail | null>(null);
    const [collections, setCollections] = useState<NetworkCollection[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [deciding, setDeciding] = useState<string[]>([]);

    const load = useCallback(async () => {
        setError(null);
        try {
            const d = await api.product(id);
            if (!d?.product) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            setDetail(d);
            if (d.product.brand) {
                api.brand(d.product.brand).then((b) => setCollections(b?.collections || [])).catch(() => setCollections([]));
            }
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_SHELL.COMMON.LOAD_FAILED'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, id]);

    useEffect(() => {
        setDetail(null);
        load();
    }, [load]);

    const togglePublish = async () => {
        if (!detail) return;
        const publish = detail.product.status !== 'published';
        setPublishing(true);
        try {
            const saved = publish ? await api.publishProduct(detail.product._id) : await api.unpublishProduct(detail.product._id);
            if (!saved?._id) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            setDetail((d) => (d ? { ...d, product: { ...d.product, ...saved } } : d));
            toast.success(publish ? t('ADMIN_NETWORK.PRODUCT.PUBLISHED') : t('ADMIN_NETWORK.PRODUCT.UNPUBLISHED'));
        } catch (e) {
            toast.error(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setPublishing(false);
        }
    };

    const decide = async (link: ProductRetailerLink, status: NetworkLinkStatus) => {
        setDeciding((ids) => [...ids, link.linkId]);
        try {
            const res = await api.setLinkStatus(link.linkId, status);
            if (!res?.link) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            if (res.affected) toast.message(t('ADMIN_NETWORK.REVIEW.AFFECTED', { count: res.affected }));
            await load();
        } catch (e) {
            toast.error(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setDeciding((ids) => ids.filter((x) => x !== link.linkId));
        }
    };

    const parents = [{ label: t('ADMIN_NETWORK.NAV'), href: '/network' }, { label: t('ADMIN_NETWORK.NAV_BRANDS'), href: '/network/brands' }];

    if (error && !detail) {
        return (
            <AdminPage title={t('ADMIN_NETWORK.PRODUCT.TITLE')} parents={parents}>
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    description={error}
                    action={<Button variant="outline" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>}
                    className="py-24"
                />
            </AdminPage>
        );
    }

    const p = detail?.product;
    const media = detail ? mediaOf(detail, t) : [];
    const [hero, ...rest] = media;
    const brandParents = detail?.brand ? [...parents, { label: detail.brand.name, href: `/network/brands/${detail.brand._id}` }] : parents;

    return (
        <AdminPage
            title={p?.name || t('ADMIN_NETWORK.PRODUCT.TITLE')}
            parents={brandParents}
            header={
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div className="min-w-0">
                        {p ? (
                            <>
                                <h1 className="font-heading text-2xl font-medium tracking-tight">{p.name}</h1>
                                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                                    {detail.brand && (
                                        <Link to={`/network/brands/${detail.brand._id}`} className="hover:text-foreground">{detail.brand.name}</Link>
                                    )}
                                    {detail.collection && <span>· {detail.collection.name}</span>}
                                    <span>·</span>
                                    <ProductStatusBadge status={p.status} />
                                </p>
                            </>
                        ) : (
                            <Skeleton className="h-8 w-64" />
                        )}
                    </div>
                    {p && (
                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" onClick={() => setEditing(true)} data-testid="product-edit">
                                <Pencil />
                                {t('ADMIN_SHELL.COMMON.EDIT')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={togglePublish}
                                disabled={publishing}
                                className={p.status === 'published' ? DESTRUCTIVE_OUTLINE : undefined}
                                data-testid="product-publish"
                            >
                                {p.status === 'published' ? <Undo2 /> : <Send />}
                                {p.status === 'published' ? t('ADMIN_NETWORK.PRODUCTS.UNPUBLISH') : t('ADMIN_NETWORK.PRODUCTS.PUBLISH')}
                            </Button>
                        </div>
                    )}
                </div>
            }
        >
          {/* `contents`: a hook for tests that leaves the page's own layout alone. */}
          <div className="contents" data-testid="product-detail">
            {!detail ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Skeleton className="h-96 w-full rounded-xl" />
                    <Skeleton className="h-96 w-full rounded-xl" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <SectionCard title={t('ADMIN_NETWORK.MEDIA.TITLE')} testId="product-media">
                            {hero ? (
                                <div className="flex flex-col gap-3">
                                    <figure className="flex flex-col gap-1.5">
                                        <ProductImageTile src={hero.url} alt={`${p.name} — ${hero.label}`} fluid openLabel={t('ADMIN_NETWORK.COMMON.ENLARGE')} emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')} className="max-w-md" />
                                        <figcaption className="text-xs text-muted-foreground">{hero.label}</figcaption>
                                    </figure>
                                    {rest.length > 0 && (
                                        <div className="flex flex-wrap gap-3">
                                            {rest.map((m) => (
                                                <figure key={m.url} className="flex flex-col gap-1">
                                                    <ProductImageTile src={m.url} alt={`${p.name} — ${m.label}`} size={112} openLabel={t('ADMIN_NETWORK.COMMON.ENLARGE')} emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')} />
                                                    <figcaption className="max-w-28 truncate text-xs text-muted-foreground">{m.label}</figcaption>
                                                </figure>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <ProductImageTile src={null} fluid emptyLabel={t('ADMIN_NETWORK.MEDIA.NONE')} className="max-w-md" />
                            )}
                        </SectionCard>

                        <SectionCard title={t('ADMIN_NETWORK.PRODUCT.ATTRIBUTES')} testId="product-attributes">
                            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.REF')}><span dir="ltr">{p.ref || '—'}</span></FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.EANS')}>
                                    <span dir="ltr" className="tabular-nums">{p.eans?.length ? p.eans.join(', ') : '—'}</span>
                                </FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.FORMAT')}><SizeLabel format={p.format} depth /></FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.COLOUR')}>
                                    <span className="inline-flex flex-wrap items-center gap-2">
                                        <ColourSwatch hex={p.colour?.hex} name={p.colour?.name} size="md" />
                                        {p.colour?.family && <span className="text-xs text-muted-foreground">{vocabLabel(t, p.colour.family)}</span>}
                                    </span>
                                </FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.FINISH')}>{vocabLabel(t, p.finish)}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.MATERIAL')}>{vocabLabel(t, p.material)}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.APPEARANCE')}>{vocabLabel(t, p.appearance)}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.PATTERN')}>{p.pattern || '—'}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.FAMILY')}>{p.familySlug || '—'}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.COLLECTION')}>{detail.collection?.name || '—'}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.FIELDS.TAGS')}>{p.tags?.length ? p.tags.join(', ') : '—'}</FieldRow>
                                <FieldRow label={t('ADMIN_NETWORK.PRODUCT.UPDATED')}>{formatDateTime(p.updatedAt || p.createdAt)}</FieldRow>
                                <FieldRow label="UUID" className="sm:col-span-2"><span dir="ltr" className="text-xs text-muted-foreground">{p.uuid}</span></FieldRow>
                            </dl>
                        </SectionCard>
                    </div>

                    <Card className="gap-0 overflow-hidden p-0" data-testid="carried-by">
                        <div className="px-6 pt-5 pb-3">
                            <p className="text-sm font-medium">{t('ADMIN_NETWORK.PRODUCT.CARRIED_BY')}</p>
                            <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.PRODUCT.CARRIED_BY_HINT', { count: detail.retailers.filter((r) => r.status === 'confirmed').length })}</p>
                        </div>
                        {detail.retailers.length ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="ps-6">{t('ADMIN_NETWORK.FIELDS.RETAILER')}</TableHead>
                                            <TableHead>{t('ADMIN_NETWORK.PRODUCT.THEIR_PRODUCT')}</TableHead>
                                            <TableHead>{t('ADMIN_NETWORK.FIELDS.METHOD')}</TableHead>
                                            <TableHead>{t('ADMIN_NETWORK.SIGNAL.OVERALL')}</TableHead>
                                            <TableHead>{t('ADMIN_NETWORK.FIELDS.STATUS')}</TableHead>
                                            <TableHead className="pe-6 text-end"><span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {detail.retailers.map((r) => {
                                            const busy = deciding.includes(r.linkId);
                                            return (
                                                <TableRow key={r.linkId} data-testid={`carried-${r.linkId}`}>
                                                    <TableCell className="ps-6 font-medium">{r.vendorName}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            <ProductImageTile src={r.imageUrl} alt={r.productName || ''} size={48} openLabel={t('ADMIN_NETWORK.COMMON.ENLARGE')} emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')} />
                                                            <div className="min-w-0">
                                                                <p className="max-w-[260px] truncate">{r.productName || t('ADMIN_NETWORK.REVIEW.RETAILER_DELETED')}</p>
                                                                <p className="text-xs text-muted-foreground" dir="ltr">{r.sku || '—'}</p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{methodLabel(t, r.method)}</TableCell>
                                                    <TableCell><OverallScoreChip score={r.score} /></TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col gap-0.5">
                                                            <LinkStatusBadge status={r.status} />
                                                            {r.decidedAt && <RelativeTime value={r.decidedAt} className="text-xs text-muted-foreground" />}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="pe-6 text-end">
                                                        <div className="flex justify-end gap-1.5">
                                                            {r.status === 'suggested' ? (
                                                                <>
                                                                    <Button variant="outline" size="sm" disabled={busy} onClick={() => decide(r, 'confirmed')} data-testid="carried-confirm">
                                                                        <Check />
                                                                        {t('ADMIN_NETWORK.REVIEW.CONFIRM')}
                                                                    </Button>
                                                                    <Button variant="outline" size="sm" className={DESTRUCTIVE_OUTLINE} disabled={busy} onClick={() => decide(r, 'rejected')} data-testid="carried-reject">
                                                                        <X />
                                                                        {t('ADMIN_NETWORK.REVIEW.REJECT')}
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <Button variant="ghost" size="sm" disabled={busy} onClick={() => decide(r, 'suggested')} data-testid="carried-reopen">
                                                                    <RotateCcw />
                                                                    {t('ADMIN_NETWORK.REVIEW.REOPEN')}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <p className="px-6 pb-6 text-sm text-muted-foreground">{t('ADMIN_NETWORK.PRODUCT.CARRIED_NONE')}</p>
                        )}
                    </Card>

                    {error && <ErrorNote message={error} onRetry={load} />}

                    <ProductEditDialog
                        open={editing}
                        onOpenChange={setEditing}
                        mode="edit"
                        product={p}
                        brandId={p.brand}
                        collections={collections}
                        onSaved={() => {
                            toast.success(t('ADMIN_NETWORK.PRODUCT.SAVED'));
                            load();
                        }}
                    />
                </>
            )}
          </div>
        </AdminPage>
    );
}

export default NetworkProductPage;
